import { SelfContainedCommand, CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import useSpaceStore from "../../../../stores/spaceStore";
import { createFileLogger } from '../../../../utils/logger';
import { useContextStore } from "../../../../components/Space/SpaceChat/store/contextStore";
import { generateDocumentPrefix } from '../../../../utils/entityIdGenerator';
import { entityEventService } from '../../../../services/entityEventService';

const log = createFileLogger('DOCS_COMMANDS', { enabled: false });

export const docsCreateCommand: SelfContainedCommand = {
    id: 'docs_create',
    description: `Create a new Document and open it for editing.

**SUPPORTED ENTITIES:**
- Documents (doc_*)
- For Notes/Sheets/Canvas/Slides, use entity_create instead

Name must be non-empty (if provided), max 100 characters. Use folderId to organize documents. Set publish=true for public blog posts (default: false).`,
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',
    
    formatViewData: (result: any, toolInput: any) => {
        try {
            if (result?.data) {
                return {
                    type: 'document_create',
                    message: `Created "${result.data.document?.name || toolInput.name || 'New Document'}"`,
                    data: {
                        document: result.data.document,
                        window: result.data.window,
                        documentId: result.data.document?.llmId || result.data.document?.id
                    }
                };
            }
            
            return {
                type: 'document_create',
                message: 'Document created successfully',
                data: {}
            };
        } catch (error) {
            console.warn('[DOCS_CREATE] Error in formatViewData:', error);
            return {
                type: 'document_create',
                message: 'Document created',
                data: {}
            };
        }
    },
    
    schema: {
        parameters: [
            {
                name: 'name',
                type: 'string',
                description: 'Document name. Be specific and descriptive. Examples: "Q4 2024 Sales Report", "Research Methods Section", "Client Proposal - Cloud Migration"',
                required: false,
                defaultValue: 'New Document',
                validator: (value: string) => {
                    if (value && value.trim().length === 0) return 'Document name cannot be empty';
                    if (value && value.length > 100) return 'Document name too long (max 100 characters)';
                    return null;
                },
                examples: [
                    'Q4 2024 Sales Report',
                    'Research Methods Section',
                    'Client Proposal - Cloud Migration',
                    'Meeting Notes - Jan 15 2025'
                ]
            },
            {
                name: 'folderId',
                type: 'string',
                description: 'Optional folder/group ID to create the document in. Use this to organize documents into folders. If not provided, document is created at the root level. Folders have entityType "Group" and IDs follow the pattern: group_{adj}_{noun}_{adj}_{noun}.',
                required: false,
                examples: [
                    'group_red_apple_blue_banana',
                    'group_bright_mountain_calm_river',
                    'group_wise_ocean_soft_breeze'
                ]
            },
            {
                name: 'publish',
                type: 'boolean',
                description: 'If true, publishes the document to the user\'s public blog (marks as in_showcase and makes publicly accessible). Default is false (private). Only use true when explicitly creating PUBLIC blog posts. Never publish drafts, notes, or planning documents.',
                required: false,
                defaultValue: false,
                examples: [
                    true,
                    false
                ]
            }
        ]
    },
    
    examples: {
        good: [
            {
                input: { name: 'Nature Neuroscience Submission' },
                expectedOutput: createSuccessResult('Document created successfully', {
                    document: {
                        id: 'doc-123',
                        name: 'Nature Neuroscience Submission',
                        entityType: 'Doc'
                    },
                    window: {
                        id: 'window-123',
                        title: 'Nature Neuroscience Submission',
                        appType: 'docs'
                    }
                }),
                description: 'Create a new document with a descriptive name',
                context: 'When starting a new document for a specific purpose'
            },
            {
                input: { name: 'Q4 2024 Budget Analysis' },
                expectedOutput: createSuccessResult('Document created successfully', {
                    document: {
                        id: 'doc-456',
                        name: 'Q4 2024 Budget Analysis',
                        entityType: 'Doc'
                    },
                    window: {
                        id: 'window-456',
                        title: 'Q4 2024 Budget Analysis',
                        appType: 'docs'
                    }
                }),
                description: 'Create a document with a specific, descriptive name',
                context: 'When creating a document for a clearly defined task'
            }
        ],
        bad: [
            {
                input: { name: '' },
                error: 'Document name cannot be empty',
                why: 'Empty names are not allowed',
                fix: 'Provide a non-empty name or omit the parameter to use default'
      },
      {
        input: { name: 'Draft Content Strategy', publish: true },
        error: 'Publishing a draft or planning document',
        why: 'Internal planning documents should never be published to the blog',
        fix: 'Omit publish parameter (defaults to false) for drafts, notes, plans'
      },
      {
        input: { name: 'Personal Notes', folderId: 'blog_folder_123', publish: true },
        error: 'Publishing personal or internal documents',
        why: 'Personal and internal documents should remain private',
        fix: 'Only set publish=true for final, polished blog posts ready for public'
            }
        ]
    },
    
    performance: {
        expectedDuration: 500,
        complexity: 'low'
    },
    
    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
        const startTime = Date.now();
        const { name = 'New Document', spaceId: providedSpaceId, initialContent, temporary, folderId, includeWelcomeImage, publish = false, createWindow = true, windowId } = params;
        
        // ✅ FIX: Get actual Space ID (not CRDT room ID with org: prefix)
        let spaceId = providedSpaceId || context.spaceId;
        if (!providedSpaceId) {
            const actualSpaceId = useSpaceStore.getState().activeSpace?.id;
            if (actualSpaceId) {
                spaceId = actualSpaceId;
                log.log('[docs_create] Using actual Space ID from spaceStore:', spaceId);
            }
        }
        
        try {
            log.log('[docs_create] Creating new document:', { name, spaceId, folderId });
            
            // Load user preferences for default font and page mode from the in-memory store.
            // Reading from the store avoids a redundant GET /user/profile network round-trip
            // on every document creation.
            let userDefaultFont = "Calibri, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
            let userDefaultPageMode: 'page' | 'pageless' = 'page';
            
            try {
                const { default: useUserStore } = await import('../../../../stores/userStore');
                const docsPrefs = useUserStore.getState().profile?.skeleton?.preferences?.docs || {};
                if (docsPrefs.defaultFont) {
                    userDefaultFont = docsPrefs.defaultFont;
                    log.log('[docs_create] Using user default font:', userDefaultFont);
                }
                if (docsPrefs.defaultPageMode) {
                    userDefaultPageMode = docsPrefs.defaultPageMode;
                    log.log('[docs_create] Using user default page mode:', userDefaultPageMode);
                }
            } catch (prefError) {
                log.warn('[docs_create] Could not load user preferences, using defaults:', prefError);
            }
            
            // Create document entity directly using EntityService
            const docMiniId = generateDocumentPrefix();
            const newDocEntityData: any = {
                entityType: 'Doc',
                name: name.trim(),
                icon: '📄',
                spaceId: spaceId,
                folderId: folderId || null,
                skeleton: {
                    '@type': 'Doc',
                    version: 'v2', // ✅ NEW: Create v2 documents by default
                    architecture: 'unified', // ✅ NEW: Use unified architecture
                    content: typeof initialContent === 'string' && initialContent.trim().length > 0 ? initialContent : '<p><span style="font-family: Calibri, sans-serif; font-size: 11pt;"></span></p>',
                    docMiniId,
                    temporary: typeof temporary === 'boolean' ? temporary : false,
                    includeWelcomeImage: includeWelcomeImage || false,
                    // CRITICAL: Set groupIds for folder hierarchy (sidebar uses this!)
                    groupIds: folderId ? [folderId] : [],
                    // If publish=true, mark document for showcase (public blog)
                    in_showcase: publish === true,
                    settings: {
                        isContentsVisible: true,
                        isChatVisible: true,
                        zoomLevel: 90,
                        defaultFont: userDefaultFont,
                        defaultPageMode: userDefaultPageMode
                    }
                },
                created_at: new Date().toISOString(),
            };
            
            // Log if publishing to blog
            if (publish === true) {
                log.log('[docs_create] 📢 Document will be published to blog (in_showcase=true)');
            }
            
            log.log('[docs_create] Creating document:', { 
                name: newDocEntityData.name,
                spaceId: newDocEntityData.spaceId,
                folderId: newDocEntityData.folderId,
                publish: publish
            });
            
            // ✅ NEW: Use entityStore.actions instead of direct service call
            const { default: useEntityStore } = await import('../../../../stores/entityStore');
            const newDoc = await useEntityStore.getState().actions.createEntity(newDocEntityData);
            
            log.log('[docs_create] Document entity created:', { 
                id: newDoc.id, 
                name: newDoc.name,
                llmId: (newDoc as any).llmId,
                folderId: folderId,
                groupIds: (newDoc as any).skeleton?.groupIds,
                fullEntity: newDoc
            });
            
            console.log('📄 [DOCS_CREATE] Document created:', {
                docId: newDoc._id || newDoc.id,
                docName: newDoc.name,
                docLlmId: (newDoc as any).llmId,
                folderId: folderId,
                groupIds: (newDoc as any).skeleton?.groupIds,
                willUpdateFolderChildEntities: !!(folderId && (newDoc._id || newDoc.id))
            });

            // ✅ CRITICAL FIX: Ensure llmIdMapper registration is complete
            const docLlmId = (newDoc as any).llmId;
            const docActualId = newDoc._id || newDoc.id;
            if (docLlmId && docActualId) {
                // Synchronously register mapping to ensure immediate availability
                const { llmIdMapper } = await import('../../../../utils/llmIdMapper');
                llmIdMapper.registerMapping(docLlmId, docActualId, 'document', 'Doc', newDoc.name);
                log.log('[docs_create] ✅ Registered llmId mapping immediately:', { docLlmId, docActualId });
            }
            
            // Update parent folder's childEntities for proper sidebar hierarchy
            if (folderId && docActualId) {
                console.log('📁 [DOCS_CREATE] Updating parent folder childEntities...', {
                    folderId,
                    docId: docActualId
                });
                
                // ✅ CRITICAL: Resolve llmId to _id if needed (supports both formats)
                const { resolveEntityId } = await import('../../../../systemCommands/entitySystem/entityIdResolver');
                const resolution = resolveEntityId(folderId);
                const resolvedFolderId = resolution.resolved;
                
                console.log('🔍 [DOCS_CREATE] Resolved folder ID:', {
                    original: folderId,
                    resolved: resolvedFolderId,
                    isLlmId: resolution.isLlmId
                });
                
                const entityStore = useEntityStore.getState();
                const parentFolder = entityStore.entities.find(e => e._id === resolvedFolderId);
                
                if (parentFolder) {
                    const currentChildren = parentFolder.skeleton?.childEntities || [];
                    console.log('📁 [DOCS_CREATE] Found parent folder:', {
                        folderId: resolvedFolderId,
                        folderName: parentFolder.name,
                        currentChildCount: currentChildren.length,
                        currentChildren: currentChildren,
                        docAlreadyInChildren: currentChildren.includes(docActualId)
                    });
                    
                    if (!currentChildren.includes(docActualId)) {
                        const updatedChildren = [...currentChildren, docActualId];
                        // ✅ FIX: Only update childEntities, don't spread cached skeleton
                        // mergeUpdate on backend will merge this with existing skeleton
                        // Use resolved folder ID (MongoDB _id) for update
                        entityStore.actions.updateEntity(resolvedFolderId, {
                            skeleton: {
                                childEntities: updatedChildren
                            }
                        });
                        console.log('✅ [DOCS_CREATE] Updated parent folder childEntities:', { 
                            folderId: resolvedFolderId,
                            folderName: parentFolder.name,
                            docId: docActualId,
                            newChildCount: updatedChildren.length,
                            updatedChildren: updatedChildren
                        });
                        log.log('[docs_create] ✅ Updated parent folder childEntities:', { 
                            folderId: resolvedFolderId, 
                            docId: docActualId, 
                            childCount: updatedChildren.length 
                        });
                        
                        // ✅ NOTE: No need to manually call refreshEdges() anymore!
                        // The dataspaceTreeStore automatically refreshes edges on entity creation events
                        // See dataspaceTreeStore.ts:373-410 for the auto-refresh implementation
                    } else {
                        console.log('⏭️ [DOCS_CREATE] Document already in folder childEntities, skipping update');
                    }
                } else {
                    console.error('❌ [DOCS_CREATE] Parent folder not found in entityStore!', {
                        originalFolderId: folderId,
                        resolvedFolderId: resolvedFolderId,
                        totalEntitiesInStore: entityStore.entities.length,
                        allFolderIds: entityStore.entities.filter(e => e.entityType === 'Group').map(e => ({ _id: e._id, llmId: (e as any).llmId, name: e.name }))
                    });
                    log.warn('[docs_create] ⚠️ Parent folder not found in entityStore:', { originalFolderId: folderId, resolvedFolderId });
                }
            } else {
                if (folderId && !docActualId) {
                    console.warn('⚠️ [DOCS_CREATE] folderId provided but docActualId missing!', { folderId, docActualId });
                }
            }
            
            // 🚀 PERFORMANCE: Initialize Y.js in background (non-blocking!)
            // The editor will initialize its own Y.Doc when opened
            // This allows docs_create to return instantly while Y.js initializes in parallel
            (async () => {
                try {
                    const { docsCRDTService } = await import('../../services/crdt/docsCRDTService');
                    const { yDocumentService } = await import('../../services/doc/internal/yDocLowLevelAPI');
                    
                    // Get the Y.js document and ensure it has proper node structure
                    const { doc: yDoc } = await docsCRDTService.getYDocForDoc(docActualId);
                    
                    if (yDoc) {
                        // Perform ID migration to ensure all nodes have proper IDs
                        yDocumentService.migrateDocumentIds(yDoc, {
                            docId: docActualId,
                            logPrefix: '[docs_create]'
                        });
                        
                        const stats = yDocumentService.getDocumentStats(yDoc);
                        
                        // If no nodes exist, ensure proper initial structure
                        if (!stats || stats.totalNodes === 0) {
                            const yxml = yDocumentService.getXmlFragment(yDoc, 'prosemirror');
                            if (yxml) {
                                const { createYElement } = await import('../../services/doc/internal/yDocLowLevelAPI');
                                
                                yDoc.transact(() => {
                                    const initialParagraph = createYElement('paragraph', {
                                        content: '',
                                        docId: docActualId,
                                        logPrefix: '[docs_create_init]'
                                    });
                                    yxml.insert(0, [initialParagraph]);
                                });
                            }
                        }
                    }
                } catch (initError) {
                    console.error('[docs_create] Y.js background init failed:', initError);
                    // Non-fatal - editor will initialize on open
                }
            })();
            
            // Mark onboarding task as completed
            
            let windowInfo = null;
            
            // Create window only if requested (default: true for backwards compatibility)
            if (createWindow !== false) {
                try {
                    // Use space_open_window command for proper multi-store coordination
                    const { useCommandRegistryStore } = await import('../../../../stores/commandRegistryStore');
                    const { executeCommand } = useCommandRegistryStore.getState();
                    
                    const entityId = newDoc._id || newDoc.id;
                    const entityLlmId = newDoc.llmId;
                    
                    console.log('[docs_create] Opening window for entity:', {
                        entityId,
                        entityLlmId,
                        entityType: newDoc.entityType,
                        name: newDoc.name
                    });
                    
                    const result = (await executeCommand('space_open_window', {
                      entityId
                    })) as import('../../../../types/standardToolResults').StandardToolResult;
                    
                    if (result.status !== 'success') {
                        log.error('[docs_create] Failed to open window:', result.message);
                        throw new Error(result.message || 'Failed to open window');
                    }
                    
                    const createdWindowId = result.data?.windowId;
                    
                    // Mark as just created so close-handler can delete if untouched
                    try {
                      const { updateWindow } = useSpaceStore.getState();
                      const window = useSpaceStore.getState().windows[createdWindowId];
                      if (window) {
                        updateWindow(createdWindowId, {
                          applicationState: {
                            ...window.applicationState,
                            docs: {
                              ...(window.applicationState?.docs || {}),
                              justCreated: true as any,
                              originalName: newDoc.name || 'New Document',
                              renamed: false as any,
                              hasContent: false as any
                            }
                          }
                        } as any);
                      }
                    } catch (stateError) {
                      log.warn('[docs_create] Failed to set justCreated state:', stateError);
                    }
                    
                    windowInfo = {
                        id: createdWindowId,
                        title: name.trim(),
                        appType: 'docs',
                        entityId,
                        entityLlmId
                    };
                    
                    log.log('[docs_create] Window created for document:', windowInfo);
                } catch (windowError) {
                    log.error('[docs_create] ❌ Failed to create window for document:', {
                        error: windowError,
                        errorMessage: windowError instanceof Error ? windowError.message : String(windowError),
                        docId: newDoc._id || newDoc.id,
                        docName: name
                    });
                    // Continue - document was created successfully even if window creation failed
                    // User can manually open the document from the sidebar
                }
            } else {
                log.log('[docs_create] Skipping window creation (createWindow=false)');
            }
            
            const result = {
                document: {
                    id: newDoc.id || newDoc._id,
                    llmId: (newDoc as any).llmId, // ✅ Should be set by entityService.create()
                    name: name.trim(),
                    entityType: newDoc.entityType || 'Doc',
                    created_at: newDoc.created_at,
                    icon: newDoc.icon || '📄'
                },
                window: windowInfo
            };
            
            log.log('[docs_create] Document created with llmId:', { 
                llmId: (newDoc as any).llmId,
                entityType: newDoc.entityType,
                hasLlmId: !!(newDoc as any).llmId
            });
            
            // ✅ PERFORMANCE FIX: Removed validation GET request
            // If entityService.create() succeeded, document exists - no need to fetch again!
            
            log.log('[docs_create] ✅ Document created successfully:', result);
            
            // Emit entity created event to propagate across all stores
            entityEventService.entityCreated(
                newDoc,
                spaceId,
                {
                    windowId: windowInfo?.id,
                    command: 'docs_create',
                    userId: context.userId
                }
            );
            
            // Only add to context when a window was actually opened — not during bulk setup
            if (windowInfo) {
                try {
                    const contextStore = useContextStore.getState();
                    contextStore.addEntityReference(newDoc.llmId || newDoc.id, {
                        title: name.trim(),
                        appType: 'docs',
                        entityType: 'Doc',
                        isActive: true,
                        windowId: windowInfo.id,
                        documentRole: 'currentDoc'
                    });
                    log.log('[docs_create] ✅ Document added to context:', {
                        llmId: newDoc.llmId || newDoc.id,
                        title: name.trim(),
                        windowId: windowInfo.id
                    });
                } catch (contextError) {
                    log.error('[docs_create] ❌ Failed to add document to context:', contextError);
                    throw contextError;
                }
            }
            
            return createSuccessResult(
                `Document created successfully: "${name.trim()}"${windowInfo ? ' (with window)' : ''} Document is ready for immediate editing with doc_write.`,
                result,
                {
                    format: 'json',
                    metadata: {
                        executionTime: Date.now() - startTime,
                        method: 'self-contained',
                        affectedEntities: [newDoc.id || newDoc._id, ...(windowInfo ? [windowInfo.id] : [])],
                        documentName: name.trim() // ✅ Add document name for UI extraction
                    } as any,
                    ui: {
                        refreshContext: true,
                        autoFocus: windowInfo?.id,
                        resultPreview: 'summary'
                    }
                }
            );
            
        } catch (error: any) {
            log.error('[docs_create] Error creating document:', error);
            return createErrorResult(
                'Document creation failed',
                `Failed to create document: ${error.message || 'Unknown error'}`,
                'execution',
                { 
                    data: { 
                        name,
                        spaceId,
                        errorStack: error instanceof Error ? error.stack : undefined
                    },
                    metadata: { executionTime: Date.now() - startTime }
                }
            );
        }
    }
};